import api from "@/utils/api";

// Simple in-memory cache + single-flight for filter-options to avoid duplicate requests
const _filterOptionsCache = new Map();
const _filterOptionsInflight = new Map();

export const getFilterOptions = async (params = {}) => {
  const companyId = params?.companyId ? String(params.companyId) : "global";
  const state = params?.state ? String(params.state) : "";
  const district = params?.district ? String(params.district) : "";
  const city = params?.city ? String(params.city) : "";
  const distributorName = params?.distributorName ? String(params.distributorName).trim() : "";
  const distributorId = params?.distributorId ? String(params.distributorId).trim() : "";
  const outletName = params?.outletName ? String(params.outletName).trim() : "";
  const key = [companyId, state, district, city, distributorName, distributorId, outletName].filter(Boolean).join("_");

  if (_filterOptionsCache.has(key)) {
    return { success: true, data: _filterOptionsCache.get(key) };
  }

  if (_filterOptionsInflight.has(key)) {
    return await _filterOptionsInflight.get(key);
  }

  // proceed to network request
  const promise = api
    .get("/dms/dashboard/filter-options", { params })
    .then((res) => {
      // API may return { data: { states, districts, cities } } or { data: { data: { ... } } }
      const optionsData = res.data?.data ?? res.data ?? {};
      try {
        _filterOptionsCache.set(key, optionsData);
      } catch (e) {
        // ignore caching errors
      }
      _filterOptionsInflight.delete(key);
      return { success: true, data: optionsData };
    })
    .catch((err) => {
      _filterOptionsInflight.delete(key);
      throw err;
    });

  _filterOptionsInflight.set(key, promise);
  return await promise;
};

export const getSKUs = async (params = {}) => {
  const response = await api.get("/dms/skus", { params });
  return response.data;
};

export const getSKUsDualContext = async (params = {}) => {
  const response = await api.get("/dms/skus/dual-context", { params });
  return response.data;
};

export const findSKUPage = async (params = {}) => {
  const response = await api.get("/dms/skus/find-page", { params });
  return response.data;
};

export const getInvoices = async (params = {}) => {
  const response = await api.get("/dms/invoices", { params });
  return response.data;
};

export const getInvoiceDetail = async (id, params = {}) => {
  const response = await api.get(`/dms/invoices/${id}`, { params });
  return response.data;
};

export const getSecondarySalesInvoices = async (params = {}) => {
  const response = await api.get("/dms/invoices/secondary-sales", { params });
  return response.data;
};

export const getSecondarySalesInvoiceDetail = async (id, params = {}) => {
  const response = await api.get(`/dms/invoices/secondary-sales/${id}`, { params });
  return response.data;
};

export const getMappedPurchaseInvoices = async (params = {}) => {
  const response = await api.get("/dms/invoices/mapped-purchases", { params });
  return response.data;
};

export const getMappedPurchaseInvoiceDetail = async (id, params = {}) => {
  const response = await api.get(`/dms/invoices/mapped-purchases/${id}`, { params });
  return response.data;
};

export const getDistributors = async (params = {}) => {
  const response = await api.get("/dms/distributors", { params });
  return response.data;
};

/** Level-scoped L1/L2/L3 sales for distributors-v2 (paginated). */
export const getHierarchySales = async (params = {}) => {
  const response = await api.get("/dms/distributors/hierarchy-sales", { params });
  return response.data;
};

export const getOutlets = async (params = {}) => {
  const response = await api.get("/dms/outlets", { params });
  return response.data;
};

export const getManufacturers = async (params = {}) => {
  const response = await api.get("/dms/manufacturers", { params });
  return response.data;
};

export const getCompanyDashboard = async (params = {}) => {
  const response = await api.get("/dms/dashboard/company-dashboard", { params });
  return response.data;
};

export const getAdminDashboard = async (params = {}) => {
  const response = await api.get("/dms/dashboard/admin", { params });
  return response.data;
};

export const getAdminSummary = async (params = {}) => {
  const response = await api.get("/dms/dashboard/admin/summary", { params });
  return response.data;
};

export const getAdminRankings = async (params = {}) => {
  const response = await api.get("/dms/dashboard/admin/rankings", { params });
  return response.data;
};

export const getSales = async (params = {}) => {
  const response = await api.get("/dms/transactions/sales", { params });
  return response.data;
};

export const getPurchase = async (params = {}) => {
  const response = await api.get("/dms/transactions/purchase", { params });
  return response.data;
};

export const getReceivable = async (params = {}) => {
  const response = await api.get("/dms/transactions/receivable", { params });
  return response.data;
};

export const getPayables = async (params = {}) => {
  const response = await api.get("/dms/transactions/payables", { params });
  return response.data;
};

export const getManufacturersFilterOptions = async (params = {}) => {
  const response = await api.get("/dms/manufacturers/filter-options", { params });
  return response.data;
};

export const getDistributorsSales = async (params = {}) => {
  const response = await api.get("/dms/distributors", { params });
  return response.data;
};

export default {
  tallyReports: {
    getFilterOptions,
    getSKUs,
    getSKUsDualContext,
    findSKUPage,
    getInvoices,
    getInvoiceDetail,
    getSecondarySalesInvoices,
    getSecondarySalesInvoiceDetail,
    getMappedPurchaseInvoices,
    getMappedPurchaseInvoiceDetail,
    getDistributors,
    getHierarchySales,
    getDistributorsSales,
    getOutlets,
    getManufacturers,
    getManufacturersFilterOptions,
    getSales,
    getPurchase,
    getReceivable,
    getPayables,
    getCompanyDashboard,
    getAdminDashboard,
    getAdminSummary,
    getAdminRankings,
  },
};
